import type { JSONContent } from '@tiptap/core';

import type {
  ArcContentItem,
  ArcDraftUpdate,
  ArcResourceType,
  ArcStandardSection,
} from '@/api/generated';

import { limitFor } from './arcSchemas';
import type { Arc, ArcItem, ArcSection, HeaderAction } from './arcTypes';

/*
 * The ARC Builder's working copy (Day 26, Save Draft; decided by Sebin on
 * 6 October). Every edit on the page is one of the pure functions below:
 * each takes the working copy and gives a new one, touching nothing else.
 * Nothing reaches the server until Save Draft sends `toBody`.
 */

/** An image chosen for the logo or the cover: stored, not yet named by the draft. */
export interface PendingImage {
  key: string;
  url: string | null;
}

export interface WorkingDraft {
  /** The ARC as the page shows it. */
  arc: Arc;
  /** Left undefined: unchanged. Null: the ARC's own image is cleared. */
  logo?: PendingImage | null;
  cover?: PendingImage | null;
}

/** Every item type; Featured and custom sections take them all (Day 27 added the last two). */
const EVERY_TYPE = [
  'CAMPAIGN',
  'GUIDE',
  'SURVEY',
  'PAGE',
  'DOCUMENT',
  'PETITION',
  'EVENT',
];

/** What each standard kind holds, as the backend's `ALLOWED_IN` says. */
const STANDARD_ACCEPTS: Record<string, string[]> = {
  FEATURED: EVERY_TYPE,
  CAMPAIGNS: ['CAMPAIGN'],
  GUIDES: ['GUIDE'],
  SURVEYS: ['SURVEY'],
  PAGES: ['PAGE'],
  DOCUMENTS: ['DOCUMENT'],
};

/** The default names the backend gives the standard kinds. */
export const STANDARD_NAMES: Record<string, string> = {
  FEATURED: 'Featured Content',
  CAMPAIGNS: 'Campaigns',
  GUIDES: 'Guides',
  SURVEYS: 'Surveys',
  PAGES: 'Pages',
  DOCUMENTS: 'Documents',
};

/** A section made on this page and not saved yet: its id is `new-<n>`. */
export function isNewSection(section: ArcSection): boolean {
  return section.id.startsWith('new-');
}

/** An item added on this page and not saved yet: its id is `new-item-<n>`. */
export function isNewItem(item: ArcItem): boolean {
  return item.id.startsWith('new-item-');
}

/** True when the section already holds this resource. */
export function holds(section: ArcSection, resourceType: string, resourceId: string) {
  return section.items.some(
    (item) => item.resource_type === resourceType && item.resource_id === resourceId,
  );
}

function ordered<T extends { position: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.position - b.position);
}

function renumbered<T extends { position: number }>(rows: T[]): T[] {
  return rows.map((row, position) => ({ ...row, position }));
}

function withArc(draft: WorkingDraft, change: (arc: Arc) => Arc): WorkingDraft {
  return { ...draft, arc: change(draft.arc) };
}

function withSections(
  draft: WorkingDraft,
  change: (sections: ArcSection[]) => ArcSection[],
): WorkingDraft {
  return withArc(draft, (arc) => ({
    ...arc,
    sections: renumbered(change(ordered(arc.sections))),
  }));
}

function withSection(
  draft: WorkingDraft,
  sectionId: string,
  change: (section: ArcSection) => ArcSection,
): WorkingDraft {
  return withSections(draft, (sections) =>
    sections.map((section) => (section.id === sectionId ? change(section) : section)),
  );
}

// ── the edits ──────────────────────────────────────────────────────────────

export function setDetails(
  draft: WorkingDraft,
  title: string,
  description: string,
): WorkingDraft {
  return withArc(draft, (arc) => ({ ...arc, title, description: description || null }));
}

/**
 * The introductory text, with the addresses of any images stored for it since
 * the last save, so the editor can show them when it is opened again.
 */
export function setIntro(
  draft: WorkingDraft,
  intro: JSONContent | null,
  images: Record<string, string> = {},
): WorkingDraft {
  return withArc(draft, (arc) => ({
    ...arc,
    header: {
      ...arc.header,
      intro_content: intro,
      intro_images: { ...arc.header.intro_images, ...images },
    },
  }));
}

export function setHeaderAction(
  draft: WorkingDraft,
  action: HeaderAction,
  enabled: boolean,
): WorkingDraft {
  return withArc(draft, (arc) => ({
    ...arc,
    header: { ...arc.header, [action]: { ...arc.header[action], enabled } },
  }));
}

/** A new logo or cover (stored already), or null to clear the ARC's own. */
export function setImage(
  draft: WorkingDraft,
  kind: 'logo' | 'cover',
  image: PendingImage | null,
): WorkingDraft {
  const shown = image?.url ? { url: image.url, own: true } : null;
  const next = withArc(draft, (arc) => ({
    ...arc,
    header: {
      ...arc.header,
      ...(kind === 'logo' ? { logo: shown } : { cover_image: shown }),
    },
  }));
  return kind === 'logo' ? { ...next, logo: image } : { ...next, cover: image };
}

/** A custom section with a name, or a deleted standard kind added back. */
/**
 * The types that have a module now, as the loaded reply shows them: every
 * section's `accepts` is its allowed types that are registered (group 6,
 * B7, D10), so their union is the registered set. With no section to read
 * it from, every type.
 */
function registeredTypes(sections: ArcSection[]): string[] {
  if (sections.length === 0) return EVERY_TYPE;
  const registered = new Set(sections.flatMap((section) => section.accepts));
  return EVERY_TYPE.filter((type) => registered.has(type));
}

export function addSection(
  draft: WorkingDraft,
  kind: 'custom' | ArcStandardSection,
  name: string,
): WorkingDraft {
  const registered = registeredTypes(draft.arc.sections);
  const taken = draft.arc.sections
    .filter(isNewSection)
    .map((section) => Number(section.id.slice(4)) || 0);
  const id = `new-${String(Math.max(0, ...taken) + 1)}`;
  const standard = kind === 'custom' ? null : kind;
  return withSections(draft, (sections) => [
    ...sections,
    {
      id,
      kind: standard ? 'STANDARD' : 'CUSTOM',
      standard_type: standard,
      name: name || (standard ? STANDARD_NAMES[standard] : ''),
      visible: true,
      position: sections.length,
      // As the reply would give them: the kind's types that are registered.
      accepts: (standard ? STANDARD_ACCEPTS[standard] : EVERY_TYPE).filter((type) =>
        registered.includes(type),
      ),
      items: [],
      // As the reply would give them once saved. Typed, not shown (D17): the
      // page counts its own copy as it is edited.
      item_limit: limitFor(standard, draft.arc.limits),
      published_item_count: 0,
    },
  ]);
}

export function renameSection(draft: WorkingDraft, sectionId: string, name: string) {
  return withSection(draft, sectionId, (section) => ({ ...section, name }));
}

export function setSectionVisible(
  draft: WorkingDraft,
  sectionId: string,
  visible: boolean,
) {
  return withSection(draft, sectionId, (section) => ({ ...section, visible }));
}

export function deleteSection(draft: WorkingDraft, sectionId: string): WorkingDraft {
  return withSections(draft, (sections) => sections.filter((s) => s.id !== sectionId));
}

/** The sections in the order of `ids`. */
export function reorderSections(draft: WorkingDraft, ids: string[]): WorkingDraft {
  return withSections(draft, (sections) =>
    ids.flatMap((id) => sections.filter((section) => section.id === id)),
  );
}

/** One section's items in the order of `ids`. */
export function reorderItems(draft: WorkingDraft, sectionId: string, ids: string[]) {
  return withSection(draft, sectionId, (section) => ({
    ...section,
    items: renumbered(
      ids.flatMap((id) => section.items.filter((item) => item.id === id)),
    ),
  }));
}

/** An item to the end of another section. */
export function moveItem(draft: WorkingDraft, itemId: string, toSectionId: string) {
  let moving: ArcItem | undefined;
  for (const section of draft.arc.sections) {
    moving ??= section.items.find((item) => item.id === itemId);
  }
  if (!moving) return draft;
  const item = moving;
  return withSections(draft, (sections) =>
    sections.map((section) => {
      const without = section.items.filter((other) => other.id !== itemId);
      const items =
        section.id === toSectionId ? [...ordered(without), item] : ordered(without);
      return { ...section, items: renumbered(items) };
    }),
  );
}

/**
 * A published resource from the Add Content picker, last in the section, with
 * the picker row's card so the section shows it at once. Its id is temporary;
 * Save Draft sends it by resource and the reply gives it a real one. Adding a
 * resource the section already holds changes nothing.
 */
export function addItem(
  draft: WorkingDraft,
  sectionId: string,
  picked: ArcContentItem,
): WorkingDraft {
  const target = draft.arc.sections.find((section) => section.id === sectionId);
  if (!target || holds(target, picked.resource_type, picked.resource_id)) return draft;
  const taken = draft.arc.sections
    .flatMap((section) => section.items)
    .filter(isNewItem)
    .map((item) => Number(item.id.slice('new-item-'.length)) || 0);
  const added: ArcItem = {
    id: `new-item-${String(Math.max(0, ...taken) + 1)}`,
    resource_type: picked.resource_type,
    resource_id: picked.resource_id,
    position: target.items.length,
    available: true,
    resource: {
      title: picked.title,
      status_label: picked.status_label,
      is_published: true,
      audience: picked.audience,
      details: picked.details ?? null,
      updated_at: picked.updated_at,
    },
  };
  return withSection(draft, sectionId, (section) => ({
    ...section,
    items: renumbered([...ordered(section.items), added]),
  }));
}

export function removeItem(draft: WorkingDraft, sectionId: string, itemId: string) {
  return withSection(draft, sectionId, (section) => ({
    ...section,
    items: renumbered(ordered(section.items).filter((item) => item.id !== itemId)),
  }));
}

// ── the request ────────────────────────────────────────────────────────────

/** The working copy as Save Draft sends it (`PUT /arc/draft`). */
export function toBody(draft: WorkingDraft): ArcDraftUpdate {
  const { arc } = draft;
  return {
    title: arc.title,
    description: arc.description ?? null,
    intro_content: arc.header.intro_content ?? null,
    donate_enabled: arc.header.donate.enabled,
    volunteer_enabled: arc.header.volunteer.enabled,
    ...(draft.logo !== undefined ? { logo_key: draft.logo?.key ?? null } : {}),
    ...(draft.cover !== undefined ? { cover_image_key: draft.cover?.key ?? null } : {}),
    // The version this copy was loaded at, not the latest one fetched: a copy
    // someone else has saved over since is refused with 409 rather than
    // silently overwriting their work (arc-and-discovery-hardening, B10).
    expected_version: arc.draft_version,
    sections: ordered(arc.sections).map((section) => ({
      ...(isNewSection(section)
        ? section.standard_type
          ? { standard_type: section.standard_type as ArcStandardSection }
          : {}
        : { id: section.id }),
      name: section.name,
      visible: section.visible,
      items: ordered(section.items).map((item) =>
        isNewItem(item)
          ? {
              resource_type: item.resource_type as ArcResourceType,
              resource_id: item.resource_id,
            }
          : { id: item.id },
      ),
    })),
  };
}

/**
 * A request body as text with every object's keys sorted. The saved
 * introduction comes back from the database with its keys in another order
 * than the editor writes them, and the same text must still compare equal.
 */
export function canonical(body: unknown): string {
  return JSON.stringify(body, (_key, value: unknown) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : value,
  );
}

/** True when the working copy would change the saved draft. */
export function differs(draft: WorkingDraft, saved: Arc): boolean {
  return contentOf(draft) !== contentOf({ arc: saved });
}

/**
 * What a save would change, without the version it is sent from: a copy
 * loaded at an older version but equal to the saved draft has nothing unsaved.
 */
function contentOf(draft: WorkingDraft): string {
  const body: Partial<ArcDraftUpdate> = toBody(draft);
  delete body.expected_version;
  return canonical(body);
}
